import { useTable } from "react-table";
import { useContext, useMemo } from "react";
import {
  defaultDateDisplay,
  formatCurrency,
  formatNumberWithCommas,
  isSellMode,
  truncateString,
} from "../../../helpers";
import primaryComponents from "../../primaryComponents";
import { useNavigate } from "react-router-dom";
import { pathConstants } from "../../../routes/pathContants";
import { exportReconciliation } from "../../../services/inventory";
import { useMutation } from "react-query";
import { ToastContext } from "../../../hooks/context";

const useReconciliationsTable = ({ reconciliations }) => {
  const statusMap = {
    APPROVED: "success--squared",
    DRAFT: "pending--squared",
    REVIEW: "pending--squared",
    REJECTED: "failed--text",
  };
  const navigate = useNavigate();
  const triggerToast = useContext(ToastContext);

  const { isLoading: isExporting, mutate: exportReconciliations } = useMutation(
    (data) => exportReconciliation(data),
  );

  const exportReconciliationList = (data) => {
    exportReconciliations(
      {
        id: data.id,
        export_type: "excel",
      },
      {
        onSuccess: (res) => {
          const { status, download_url } = res?.data?.data || {};

          if (status === "completed" && download_url) {
            window.open(download_url, "_blank");
          }

          // "queued" (or any other status) has no file yet — just surface the detail message
          triggerToast(res?.data?.detail, "success");
        },
      },
    );
  };

  const columns = useMemo(
    () => [
      {
        Header: "Date/Time",
        accessor: "created_at",
        Cell: ({ cell: { value } }) =>
          value ? defaultDateDisplay(value) : "N/A",
      },
      {
        Header: "Created By",
        accessor: "created_by",
        Cell: ({ cell: { value } }) =>
          value ? truncateString(value?.first_name, 50) : "N/A",
      },
      {
        Header: "Approved By",
        accessor: "reviewed_by",
        Cell: ({ cell: { value } }) =>
          value ? truncateString(value?.first_name, 50) : "N/A",
      },
      {
        Header: "Store",
        accessor: "store",
        Cell: ({ cell: { value } }) => value?.name,
      },
      {
        Header: "Discrepancies",
        accessor: "discrepancy",
        Cell: ({ cell: { value } }) => formatNumberWithCommas(value),
      },
      {
        Header: "Estimated Impact",
        accessor: "discrepancy_impact",
        Cell: ({ cell }) => {
          return formatCurrency({
            value: cell.value,
            currencyCode: cell?.row?.original?.store?.currency_code,
          });
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ cell: { value } }) => (
          <span className={`${statusMap[value]} text-capitalize`}>{value}</span>
        ),
      },

      {
        Header: "Action",
        Cell: ({ row }) => (
          <span className="d-flex">
            <primaryComponents.Button
              classNames={"btn btn--outline-green smallBtn mr-1"}
              onClick={() =>
                exportReconciliationList({ id: row?.original?.id })
              }
              isDisabled={isExporting}
            >
              Export
            </primaryComponents.Button>
            {row?.original.status !== "DRAFT" ? (
              <primaryComponents.Button
                onClick={() =>
                  navigate(
                    pathConstants.INVENTORY_RECONCILIATIONS_REVIEW({
                      store_id: row?.original?.store?.id,
                      id: row?.original?.id,
                      isSellMode: isSellMode(),
                    }) + `?store_id=${row?.original?.store?.id}`,
                  )
                }
                classNames={"btn btn--primary smallBtn mr-2"}
              >
                Review
              </primaryComponents.Button>
            ) : (
              <primaryComponents.Button
                onClick={() => {
                  navigate(
                    pathConstants.INVENTORY_RECONCILIATIONS_EDIT({
                      store_id: row?.original?.store?.id,
                      id: row?.original?.id,
                      isSellMode: isSellMode(),
                    }) + `?store_id=${row?.original?.store?.id}`,
                  );
                }}
                classNames={"btn btn--outline smallBtn"}
              >
                Edit
              </primaryComponents.Button>
            )}
          </span>
        ),
      },
    ],
    [],
  );

  const instance = useTable({ columns, data: reconciliations });
  return {
    instance,
  };
};

export default useReconciliationsTable;
